import React, { useState, useMemo } from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
} from "@mui/material";
import {
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  LocalShipping as ShippingIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
} from "@mui/icons-material";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useOrders } from "../features/orders/hooks/useOrders";
import { Order, OrderStatus } from "../types";
import { DashboardLayout } from "../app/layout/DashboardLayout";

interface Column {
  id: keyof Order;
  label: string;
  minWidth?: number;
  align?: "right" | "left" | "center";
  format?: (value: any) => string;
}

const Orders: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState<"view" | "edit" | "assign">("view");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "");

  // Use the custom hook
  const {
    orders,
    isLoading,
    error,
    updateFilters,
    updateOrderStatus,
    assignDriver,
    isUpdating,
    isAssigning,
  } = useOrders({
    status: statusFilter || undefined,
    salesperson_id: user?.role === "salesperson" ? user.id : undefined,
    page: 1,
    limit: 10,
  });

  const statusColors: { [key: string]: "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" } = {
    pending_approval: "warning",
    approved: "info",
    in_production: "primary",
    ready_for_delivery: "secondary",
    out_for_delivery: "warning",
    delivered: "success",
    cancelled: "error",
  };

  const statusLabels: { [key: string]: string } = {
    pending_approval: "Onay Bekliyor",
    approved: "Onaylandı",
    in_production: "Üretimde",
    ready_for_delivery: "Teslimata Hazır",
    out_for_delivery: "Yolda",
    delivered: "Teslim Edildi",
    cancelled: "İptal Edildi",
  };

  const handleStatusChange = (event: any) => {
    const newStatus = event.target.value;
    setStatusFilter(newStatus);
    setSearchParams(newStatus ? { status: newStatus } : {});
    updateFilters({ status: newStatus || undefined });
  };

  const handleViewOrder = (order: Order) => {
    navigate(`/orders/${order.id || order._id}`);
  };

  const handleEditOrder = (order: Order) => {
    setSelectedOrder(order);
    setDialogType("edit");
    setDialogOpen(true);
  };

  const handleAssignDriver = (order: Order) => {
    setSelectedOrder(order);
    setDialogType("assign");
    setDialogOpen(true);
  };

  const handleUpdateStatus = async (orderId: string, newStatus: Order["status"]) => {
    try {
      await updateOrderStatus(orderId, newStatus);
      setDialogOpen(false);
    } catch (error) {
      console.error("Error updating order status:", error);
    }
  };

  const handleAssignDriverToOrder = async (orderId: string, driverId: string) => {
    try {
      await assignDriver(orderId, driverId);
      setDialogOpen(false);
    } catch (error) {
      console.error("Error assigning driver:", error);
    }
  };

  const canEditOrder = (order: Order) => {
    if (user?.role === "admin") {
      return true;
    }
    if (user?.role === "manager") {
      return ["pending_approval"].includes(order.status);
    }
    if (user?.role === "warehouse") {
      return ["approved"].includes(order.status);
    }
    if (user?.role === "driver") {
      return ["ready_for_delivery", "out_for_delivery"].includes(order.status);
    }
    if (user?.role === "salesperson") {
      // Satıcılar sadece kendi siparişlerini düzenleyebilir
      const isOwnOrder = order.salesperson === user.full_name || 
        (typeof order.salesperson_id === 'object' ? order.salesperson_id.id : order.salesperson_id) === user.id;
      return isOwnOrder && ["pending", "pending_approval"].includes(order.status);
    }
    return false;
  };

  const filteredOrders = useMemo(() => {
    let filtered = orders;
    
    // Satıcılar sadece kendi siparişlerini görsün
    if (user?.role === "salesperson") {
      filtered = filtered.filter((order) => 
        order.salesperson === user.full_name || 
        (typeof order.salesperson_id === 'object' ? order.salesperson_id.id : order.salesperson_id) === user.id
      );
    }
    
    // Arama filtresi
    if (searchTerm) {
      filtered = filtered.filter((order) =>
        order.order_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.customer_name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    return filtered;
  }, [orders, searchTerm, user]);

  const columns: Column[] = [
    {
      id: "order_number",
      label: "Sipariş No",
      minWidth: 120,
    },
    {
      id: "customer_name",
      label: "Müşteri",
      minWidth: 150,
    },
    {
      id: "total_amount",
      label: "Toplam",
      minWidth: 100,
      align: "right",
      format: (value: number) => `${value.toFixed(2)} TL`,
    },
    {
      id: "status",
      label: "Durum",
      minWidth: 120,
      format: (value: string) => statusLabels[value] || value,
    },
    {
      id: "created_at",
      label: "Tarih",
      minWidth: 120,
      format: (value: string) => new Date(value).toLocaleDateString("tr-TR"),
    },
  ];

  if (error) {
    return (
      <DashboardLayout>
        <Container maxWidth="xl">
          <Alert severity="error" sx={{ mt: 2 }}>
            Siparişler yüklenirken hata oluştu: {error.message}
          </Alert>
        </Container>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <Container maxWidth="xl">
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" gutterBottom>
            {user?.role === "salesperson" ? "Siparişlerim" : "Siparişler"}
          </Typography>
        </Box>

        {/* Filters */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
            <TextField
              label="Ara"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ minWidth: 200 }}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />,
              }}
            />

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Durum</InputLabel>
              <Select
                value={statusFilter}
                label="Durum"
                onChange={handleStatusChange}
              >
                <MenuItem value="">Tümü</MenuItem>
                <MenuItem value="pending_approval">Onay Bekliyor</MenuItem>
                <MenuItem value="approved">Onaylandı</MenuItem>
                <MenuItem value="in_production">Üretimde</MenuItem>
                <MenuItem value="ready_for_delivery">Teslimata Hazır</MenuItem>
                <MenuItem value="out_for_delivery">Yolda</MenuItem>
                <MenuItem value="delivered">Teslim Edildi</MenuItem>
                <MenuItem value="cancelled">İptal Edildi</MenuItem>
              </Select>
            </FormControl>

            <Button
              variant="outlined"
              startIcon={<FilterIcon />}
              onClick={() => {
                setSearchTerm("");
                setStatusFilter("");
                setSearchParams({});
                updateFilters({});
              }}
            >
              Filtreleri Temizle
            </Button>
          </Box>
        </Paper>

        {/* Orders Table */}
        <Paper sx={{ width: "100%", overflow: "hidden" }}>
          {isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer sx={{ maxHeight: 440 }}>
                <Table stickyHeader aria-label="orders table">
                  <TableHead>
                    <TableRow>
                      {columns.map((column) => (
                        <TableCell
                          key={column.id}
                          align={column.align}
                          style={{ minWidth: column.minWidth }}
                        >
                          {column.label}
                        </TableCell>
                      ))}
                      <TableCell align="center" style={{ minWidth: 120 }}>
                        İşlemler
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredOrders.map((order) => (
                      <TableRow hover key={order._id}>
                        {columns.map((column) => {
                          const value = order[column.id];
                          return (
                            <TableCell key={column.id} align={column.align}>
                              {column.id === "status" ? (
                                <Chip
                                  label={column.format ? column.format(value) : String(value || '')}
                                  color={statusColors[value as OrderStatus] || "default"}
                                  size="small"
                                />
                              ) : (
                                column.format ? column.format(value) : String(value || '')
                              )}
                            </TableCell>
                          );
                        })}
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            onClick={() => handleViewOrder(order)}
                            title="Görüntüle"
                          >
                            <VisibilityIcon />
                          </IconButton>
                          {canEditOrder(order) && (
                            <IconButton
                              size="small"
                              onClick={() => handleEditOrder(order)}
                              title="Düzenle"
                            >
                              <EditIcon />
                            </IconButton>
                          )}
                          {user?.role === "manager" && order.status === "approved" && (
                            <IconButton
                              size="small"
                              onClick={() => handleAssignDriver(order)}
                              title="Sürücü Ata"
                            >
                              <ShippingIcon />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {filteredOrders.length === 0 && !isLoading && (
                <Box sx={{ p: 3, textAlign: "center" }}>
                  <Typography variant="body1" color="text.secondary">
                    Sipariş bulunamadı
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Paper>

        {/* Order Details Dialog */}
        <Dialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            {dialogType === "view" && "Sipariş Detayları"}
            {dialogType === "edit" && "Sipariş Düzenle"}
            {dialogType === "assign" && "Sürücü Ata"}
          </DialogTitle>
          <DialogContent>
            {selectedOrder && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="h6">Sipariş #{selectedOrder.order_number}</Typography>
                <Typography variant="body1">Müşteri: {selectedOrder.customer_name}</Typography>
                <Typography variant="body1">Toplam: {selectedOrder.total_amount.toFixed(2)} TL</Typography>
                <Typography variant="body1">Durum: {statusLabels[selectedOrder.status]}</Typography>
                {/* Add more order details here */}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>Kapat</Button>
            {dialogType === "edit" && selectedOrder && (
              <Button
                variant="contained"
                disabled={isUpdating}
                onClick={() => handleUpdateStatus(selectedOrder._id, "approved")}
              >
                {isUpdating ? <CircularProgress size={20} /> : "Onayla"}
              </Button>
            )}
            {dialogType === "assign" && selectedOrder && (
              <Button
                variant="contained"
                disabled={isAssigning}
                onClick={() => handleAssignDriverToOrder(selectedOrder._id, "driver-id")}
              >
                {isAssigning ? <CircularProgress size={20} /> : "Sürücü Ata"}
              </Button>
            )}
          </DialogActions>
        </Dialog>
      </Container>
    </DashboardLayout>
  );
};

export default Orders;
